/**
 * PlanSection
 *
 * Displays the current user plan and upgrade/manage actions. Accepts
 * plan level/interval, status messages, and enterprise flag.
 */
import React from 'react';
import { Zap, Check, Crown } from 'lucide-react';
import { getPlanDisplayName, getPlanFeatures } from '../../../data/pricing/pricingPlans';

export default function PlanSection({
  userPlanLevel,
  userPlanInterval,
  planSuccessMessage,
  handleNavigateToPricing,
  subscriptionPeriodEnd,
  formatDate,
  isEnterpriseUser
}) {
  // Get interval display for current plan
  const intervalDisplay = userPlanInterval 
    ? (userPlanInterval === 'annual' ? 'annually' : 'monthly')
    : '';
  
  // Get display name for the plan with interval if available
  const planDisplayName = `${getPlanDisplayName(userPlanLevel)}${intervalDisplay ? ` (${intervalDisplay})` : ''}`;

  // Check if user is on premium plan
  const isPremium = userPlanLevel === 'premium';
  
  // Get plan features
  const currentPlanFeatures = getPlanFeatures(userPlanLevel);

  return (
    <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-[300px,1fr]">
      <div className="py-6 text-center md:text-left">
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">Current Plan</h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Manage your subscription and billing details.
        </p>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-800 dark:bg-gray-900">
        {planSuccessMessage && (
          <div className="mb-6 flex items-center justify-center sm:justify-start gap-2 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700 dark:bg-green-900/30 dark:text-green-400">
            <Check className="h-4 w-4 flex-shrink-0" />
            <span>{planSuccessMessage}</span>
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div className="text-center sm:text-left">
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-2">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {planDisplayName}
              </h3>
              <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset 
                ${isPremium 
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white ring-purple-500/20' 
                    : 'bg-primary-50 text-primary-700 ring-primary-600/20 dark:bg-primary-900/30 dark:text-primary-400 dark:ring-primary-500/20'
                }`}>
                Current Plan
              </span>
            </div>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {userPlanLevel === 'standard' || userPlanLevel === 'basic'
                ? 'Free tier with basic features'
                : 'Premium tier with all features'}
            </p>
            {/* Enterprise support contact message */}
            {isEnterpriseUser && (
              <div className="mt-4 p-4 rounded-lg border border-gray-200 bg-gray-50/50 dark:border-gray-700 dark:bg-gray-800/30">
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    Enterprise Plan Management
                  </h4>
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                    Your plan is managed at the enterprise level. To make changes such as upgrading, 
                    downgrading, or modifying your subscription, please have your administrator 
                    contact our support team.
                  </p>
                  <div className="flex items-center gap-2 pt-1">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Contact:</span>
                    <a 
                      href="mailto:support@advocacylab.io" 
                      className="text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline transition-colors"
                    >
                      support@advocacylab.io
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Only show upgrade box for NON-enterprise users */}
        {!isEnterpriseUser && (
          <div className="mt-8">
            <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-transparent">
              <div className="flex flex-col p-4 sm:p-6 sm:flex-row sm:items-start sm:gap-4">
                <div className={`mx-auto mb-4 rounded-full p-2.5 sm:mx-0 sm:mb-0
                  ${isPremium
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500' 
                    : 'bg-primary-100 dark:bg-primary-900/50'
                  }`}>
                  <Crown className={`h-5 w-5 ${isPremium ? 'text-white' : 'text-primary-text-600 dark:text-primary-text-400'}`} />
                </div>
                <div className="flex-1">
                  {(userPlanLevel === 'standard' || userPlanLevel === 'basic') && (
                    <h3 className="text-center text-lg font-semibold text-gray-900 dark:text-white sm:text-left">
                      Upgrade to Premium
                    </h3>
                  )}
                  {userPlanLevel === 'premium' && (
                    <h3 className="text-center text-lg font-semibold text-gray-900 dark:text-white sm:text-left">
                      Manage Your Subscription
                    </h3>
                  )}

                  <p className="mt-1 text-center text-sm text-gray-600 dark:text-gray-400 sm:text-left">
                    {userPlanLevel === 'standard' || userPlanLevel === 'basic'
                      ? 'Access to view templates without editing capabilities'
                      : 'View and manage your premium subscription'}
                  </p>

                  {(userPlanLevel === 'standard' || userPlanLevel === 'basic') && (
                    <>
                      <p className="mb-4 text-sm text-gray-600 dark:text-gray-400 text-center sm:text-left">
                        Upgrade to Premium for significant improvements:
                      </p>
                      <ul className="mt-6 space-y-4 sm:mt-4 sm:space-y-3 px-2 sm:px-0">
                        {getPlanFeatures('premium').map((feature, index) => (
                          <li key={index} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                          <Check className="h-4 w-4 text-primary-text-600 dark:text-primary-text-400 flex-shrink-0" />
                            <span>{feature}</span>
                        </li>
                        ))}
                      </ul>
                      <div className="mt-8 flex flex-col items-center gap-4 sm:mt-6 sm:flex-row">
                        <button
                          onClick={handleNavigateToPricing}
                          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors"
                        >
                          <Zap className="h-4 w-4 flex-shrink-0" />
                          Upgrade to Premium
                        </button>
                        <div className="text-center sm:text-left">
                          <p className="font-medium text-gray-900 dark:text-white">$49/month</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">or $470/year (save 20%)</p>
                        </div>
                      </div>
                    </>
                  )}



                  {userPlanLevel === 'premium' && (
                    <div className="mt-6">
                      <p className="mb-4 text-sm text-gray-600 dark:text-gray-400 text-center sm:text-left">
                        You're currently on our Premium Plan. Enjoy all the premium features including custom templates, AI-powered tools, and priority support!
                      </p>
                      <div className="mt-6 flex flex-col sm:flex-row gap-3 items-center sm:items-stretch">
                        <button
                          onClick={handleNavigateToPricing}
                          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors"
                        >
                          Manage Subscription
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}